// What a host hands the agents views (ADR 0060: the -react part of an SDK takes its client and the host's
// providers, never the host's modules). A host passes its query cache and the scopes of its keys, its page paths,
// its Markdown renderer, its runs and chat, and a build-request button; another app passes its own, or leaves the
// optional ones out. Every port and path is the host's for the one place the views are mounted at: the views never
// name where they are.
import { useQuery, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { AgentsClient } from '@cortex/sdk-agents-client';
import * as React from 'react';
import type { LedgerRun, ModelRole } from './model.ts';
import type { EvalComparison, EvalModels, EvalRun, EvalRunResult, EvalSuite } from './Evaluations.tsx';

/**
 * Agent evaluations (ADR 0035): the suites of the app (its `agent` tests, grouped by agent and suite), the
 * runs with their parameters, a run with chosen parameters, two runs compared, and the models a run may pick.
 * A host: its tests and its LLM port. Absent = the views show no Evaluations.
 */
export interface AgentEvalsPort {
  suites(): Promise<EvalSuite[]>;
  models(): Promise<EvalModels>;
  runs(q: { subject: string }): Promise<EvalRun[]>;
  run(q: { id: number }): Promise<{ run: EvalRun; results: EvalRunResult[] }>;
  start(q: {
    keys: string[];
    model?: string;
    role?: string;
    /** A harness over the agent's own for this run (ADR 0039 §6), recorded with its parameters. */
    harness?: Record<string, unknown>;
  }): Promise<EvalRun>;
  compare(q: { a: number; b: number }): Promise<EvalComparison>;
  /** Deletes a case from the app (one change; past runs keep their results); absent = no Delete. */
  removeCase?(q: { key: string }): Promise<void>;
  /** Turns a case off (kept in the app, not run) or on again (one change); absent = no Disable. */
  setCaseEnabled?(q: { key: string; enabled: boolean }): Promise<void>;
  /** Saves new cases as `agent` tests of the app (one change); absent = no "+ Add case". */
  addCases?(q: {
    tests: { key: string; name: string; kind: 'agent'; spec: Record<string, unknown> }[];
  }): Promise<void>;
}

/** One event of a test-chat turn (the assistant's stream vocabulary; an ask answer maps onto it). */
export type AgentTurnEvent =
  | { type: 'session'; sessionId: string; title?: string }
  | { type: 'run_start'; agent: string; label: string; task: string; parent: string | null }
  | { type: 'tool_call'; agent: string; id: string; name: string; input: Record<string, unknown> }
  | {
      type: 'tool_result';
      agent: string;
      id: string;
      name: string;
      status: 'done' | 'failed';
      summary: string;
      ms: number;
    }
  | {
      type: 'run_end';
      agent: string;
      status: string;
      guardrail: unknown;
      inputTokens?: number;
      outputTokens?: number;
      /** How the run ended (ADR 0039 §4) and the version of the harness it ran on. */
      outcome?: string;
      harness?: string;
    }
  | { type: 'text'; delta: string }
  | {
      type: 'done';
      status: 'ok' | 'refused' | 'error' | 'stopped';
      text: string;
      usage?: { inputTokens: number; outputTokens: number; costUsd: number | null };
      /** How the turn's run ended (ADR 0039 §4), the limit or check that stopped it, a call waiting for approval. */
      outcome?: string;
      stop?: { reason: string; budget?: string; check?: string };
      approval?: { tool: string; input: Record<string, unknown>; key: string };
    }
  | { type: 'error'; message: string; status: number };

/** How the agent page's chat reaches the agent: one turn, as a stream of events. */
export interface AgentChatPort {
  turn(q: {
    agent: string;
    message: string;
    sessionId: string | null;
    signal: AbortSignal;
    /** Tool calls the person approved (ADR 0039 §5): the keys a `needs_human` answer named. */
    approvals?: string[];
  }): AsyncIterable<AgentTurnEvent>;
}

/** The runs an agent made (a host's ledger of runs, persona `agent-<name>`). */
export type AgentRunsPort = (q: {
  persona: string;
  /** Everyone's runs (the caller manages the agent), else the caller's own. */
  all: boolean;
  limit: number;
}) => Promise<LedgerRun[]>;

/** Where a project's model choice is set (a host: its AI settings, its organization's, the platform's). */
export type ModelSetIn = 'project' | 'organization' | 'platform';

/** What each model role of a project answers with now, and where each is set. */
export interface ProjectRoleModels {
  /** Who answers: the platform's AI, or the organization's own provider (`provider`). */
  source: 'platform' | 'own';
  sourceSetIn: ModelSetIn;
  provider: string | null;
  roles: { role: ModelRole; model: string | null; setIn: ModelSetIn }[];
  /** Why the live models could not be read (the roles then show what is configured). */
  error: string | null;
}

/** The concrete model behind each role (a host's AI settings). Absent = the Model tab shows roles only. */
export interface AgentModelsPort {
  resolve(): Promise<ProjectRoleModels>;
  /** The host's page where that level's AI settings are; null = no page to link. */
  settingsPath?(q: { setIn: ModelSetIn }): string | null;
}

export interface AgentsHost {
  client: AgentsClient;
  /** The host's query cache: the views' data lives under `agentsKey`/`agentRunsKey`, so the host can mark it stale. */
  queryClient: QueryClient;
  /**
   * The scopes of the views' cache keys here: `agents` prefixes the agents' data (`agentsKey`), `runs` the runs
   * (`agentRunsKey`). A host puts them under its own scopes, so marking those stale reloads the views.
   */
  cache: { agents: QueryKey; runs: QueryKey };
  /** Where the module's pages are on the host (the router's paths). */
  paths: {
    agents(): string;
    agent(key: string, query?: string): string;
    /** A file of the app's sources at a line (Used in opens a call there); absent = the file is plain text. */
    code?(file: string, line: number): string;
    /** A workflow with one step picked (Used in opens a step there); absent = no link. */
    workflow?(key: string, step?: string): string;
    /** An app (Used in opens an app's chat there); absent = no link. */
    app?(app: string): string;
  };
  /** The lines a host's chat tells a platform persona where it runs (the Instructions tab shows them). */
  session?: string[];
  /** Renders an answer's Markdown. */
  Markdown: React.ComponentType<{ source: string }>;
  /** The agent page's chat; absent = the SDK's ask route, one answer per turn. */
  chat?: AgentChatPort;
  /** The runs list of the agent page; absent = no runs section. */
  runs?: AgentRunsPort;
  /** Agent evaluations (ADR 0035); absent = no Evaluations on the Agents and agent pages. */
  evals?: AgentEvalsPort;
  /** The concrete model each role resolves to for a project; absent = the Model tab names the role only. */
  models?: AgentModelsPort;
  /**
   * The host's dialog to have a new agent built from a description (e.g. a board's request to build one); the rail's
   * "New agent" then offers it ("Describe it") beside an empty agent. Absent = "New agent" makes an empty agent.
   */
  BuildAgentRequest?: React.ComponentType<BuildAgentRequestProps>;
  /** Where the host's platform agents work (e.g. "the assistant's chat"); absent: "the host's chat". */
  platformChat?: string;
  /** The host's own name as its people know it: who hands a platform agent a question. Absent: "the host". */
  platform?: string;
  /**
   * What each group of the host's tools changes, in plain words (a platform agent's "Changes …" entry, by the tool's
   * `group`); a group without words is named as it is.
   */
  toolChanges?: Record<string, string>;
  /**
   * Files a request for the builder to make a new agent (e.g. a card on a board) — a project agent
   * made from a platform agent; answers where the request can be seen (opened after). Absent = the page opens
   * `BuildAgentRequest` filled in instead.
   */
  requestAgent?(q: { title: string; details: string }): Promise<{ path: string }>;
  /** @deprecated A host button shown under "New agent"; `BuildAgentRequest` puts the builder in its menu instead. */
  buildRequest?: React.ReactNode;
  /**
   * A change request for one agent (a host's dialog, which may then open what it made); absent = the views offer no "Build a change".
   */
  ChangeRequest?: React.ComponentType<ChangeRequestProps>;
}

/**
 * The props of the host's new-agent dialog: how to close it, and — when it starts from a platform agent (a project
 * agent made from it) — its heading, title and brief, filled in.
 */
export interface BuildAgentRequestProps {
  onClose(): void;
  heading?: string;
  title?: string;
  details?: string;
}

/** The props of the host's change-request dialog: the agent's key and title, and how to close it. */
export interface ChangeRequestProps {
  agent: string;
  title: string;
  onClose(): void;
}

const Ctx = React.createContext<AgentsHost | null>(null);

export function AgentsProvider({ host, children }: { host: AgentsHost; children: React.ReactNode }) {
  return <Ctx.Provider value={host}>{children}</Ctx.Provider>;
}

export function useAgentsHost(): AgentsHost {
  const h = React.useContext(Ctx);
  if (!h) throw new Error('The agents views need an <AgentsProvider host={…}>');
  return h;
}

/**
 * The cache keys of the views: `[...scope, 'agents', …]` sits under the host's scope (`cache.agents`), so a host
 * that marks that scope stale (a chat edit) reloads them too.
 */
export const agentsKey = (scope: QueryKey, ...extra: unknown[]) => [...scope, 'agents', ...extra] as const;
/** The runs of the agents (`cache.runs`: runs outlive a version of the definitions). */
export const agentRunsKey = (scope: QueryKey, ...extra: unknown[]) =>
  ['agent-runs', ...scope, ...extra] as const;

/** The agents' cache key here (`agentsKey` under the host's scope). */
export function useAgentsKey() {
  const { cache } = useAgentsHost();
  return React.useCallback((...extra: unknown[]) => agentsKey(cache.agents, ...extra), [cache.agents]);
}

export interface AgentsQuery<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** A cached load on the host's query cache. */
export function useAgentsQuery<T>(
  key: QueryKey,
  fn: () => Promise<T>,
  opts: { enabled?: boolean } = {},
): AgentsQuery<T> {
  const { queryClient } = useAgentsHost();
  const q = useQuery(
    { queryKey: key, queryFn: fn, ...(opts.enabled === undefined ? {} : { enabled: opts.enabled }) },
    queryClient,
  );
  const { refetch } = q;
  const reload = React.useCallback(() => void refetch(), [refetch]);
  return {
    data: q.data ?? null,
    error: q.error ? (q.error instanceof Error ? q.error.message : String(q.error)) : null,
    loading: q.isFetching || (q.isPending && opts.enabled !== false),
    reload,
  };
}

/** Marks the views' queries stale: those on screen reload at once. */
export function useInvalidate() {
  const { queryClient } = useAgentsHost();
  return React.useCallback(
    (keys: readonly QueryKey[]) => {
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    },
    [queryClient],
  );
}

/** The chat of a host without one: the SDK's ask route, each answer shown as one turn. */
export function askChat(client: AgentsClient): AgentChatPort {
  return {
    async *turn(q) {
      try {
        const a = await client.ask(q.agent, { message: q.message });
        const agent = `agent-${q.agent}`;
        for (const [i, c] of a.toolCalls.entries()) {
          yield { type: 'tool_call', agent, id: `t${i}`, name: c.name, input: c.input };
          yield {
            type: 'tool_result',
            agent,
            id: `t${i}`,
            name: c.name,
            status: c.status === 'done' ? 'done' : 'failed',
            summary: `${c.name}: ${c.status}`,
            ms: c.ms,
          };
        }
        yield {
          type: 'done',
          status: a.status === 'ok' ? 'ok' : a.status === 'refused' ? 'refused' : 'error',
          text: a.text,
          ...(a.outcome ? { outcome: a.outcome } : {}),
          ...(a.approval ? { approval: a.approval } : {}),
        };
      } catch (e) {
        const status = (e as { status?: number }).status ?? 0;
        yield {
          type: 'error',
          message:
            status === 401
              ? 'Your session has ended; sign in again.'
              : `The agent could not answer: ${(e as Error).message}`,
          status,
        };
      }
    },
  };
}
